import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { buildPriorities, CaseloadReading, IAgencyAlert, IAgencyDigest } from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { BeaSays } from './BeaSays';
import { InsightsCard } from './InsightsCard';
import { InsightsInfoTip } from './InsightsInfoTip';
import { InsightsNarration } from './InsightsNarration';
import { NeedsAttentionReport } from './NeedsAttentionReport';
import { WorthWatchingList } from './WorthWatchingList';

/** How many clients the Worth Watching section lists before it stops. */
const MAX_WORTH_WATCHING = 8;

const useStyles = createUseStyles({
    section: {
        marginTop: 16,
        paddingTop: 12,
        borderTop: `1px solid ${BRAND.tealEdge}`
    },

    toggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '4px 0',
        border: 0,
        background: 'none',
        font: 'inherit',
        fontSize: 13.5,
        fontWeight: 600,
        color: BRAND.deepTeal,
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            textDecoration: 'underline'
        }
    },

    count: {
        padding: '1px 8px',
        borderRadius: 20,
        background: BRAND.tealWash,
        border: `1px solid ${BRAND.tealEdge}`,
        fontSize: 12,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums'
    },

    hint: {
        marginLeft: 'auto',
        fontSize: 12.5,
        fontWeight: 500,
        color: CHART_INK.label
    },

    expanded: {
        marginTop: 10
    }
});

/**
 * The Needs Attention report as the page shows it: the card, the few clients to deal with first,
 * Bea's line under them, and folded beneath, everyone worth watching and why, the ones listed
 * above included.
 *
 * The whole caseload's few whatever the page is narrowed to, since what to deal with first does not
 * change with how the page is being read, except narrowed to the clients drawn red, when the list
 * holds every one of them: nothing else on the page lists them then. The Worth Watching section does
 * follow the narrowing, since it is the worklist behind the map's yellow cells.
 */
export const NeedsAttentionCard: FC<{
    digest: IAgencyDigest;
    /** Every finding in scope, which the Worth Watching section groups by client. */
    alerts: IAgencyAlert[];
    /** Whether the reader narrowed the findings to one kind, which leaves out a client with none of that kind. */
    kindNarrowed: boolean;
    /** The reading the page is narrowed to, when it is. */
    readingNarrowed?: CaseloadReading;
    /** When the page is narrowed, the clients still in scope. */
    matchingIds?: string[];
    narrowed: boolean;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    clientReading: (careMapId: string) => CaseloadReading | undefined;
    onOpenClient: (careMapId: string) => void;
    /** Narrows the page to the clients drawn red. */
    onShowIncidents: () => void;
}> = ({ digest, alerts, kindNarrowed, readingNarrowed, matchingIds, narrowed, clientAvatar, clientReading, onOpenClient, onShowIncidents }) => {
    const classes = useStyles();
    const narration = useInstance(InsightsNarration);
    // Folded by default: the few at the top are the point of the card, and the rest are there for
    // a reader who wants to go through everyone.
    const [showWatching, setShowWatching] = useState(false);

    const priorities = buildPriorities(digest, { listEveryIncident: readingNarrowed === 'incident' });
    const watching = narration.worthWatching({ baselines: digest.baselines, alerts, matchingIds, includeQuiet: !kindNarrowed });
    const listed = watching.slice(0, MAX_WORTH_WATCHING);

    return (
        <InsightsCard
            heading="Needs Attention"
            action={<InsightsInfoTip label="About who needs attention" summary="The three or four clients to deal with first: everyone with an incident in the recent weeks, newest first, and with fewer than three of those, the clients worth watching who are furthest above their own usual. Each says why. Under them, everyone worth watching: clients with more raised about them recently than in their own earlier weeks, newest finding first, and why in the words of Bea's findings about them." />}
        >
            <NeedsAttentionReport
                clients={priorities.listed}
                unlistedIncidents={priorities.unlistedIncidents}
                clientAvatar={clientAvatar}
                onSelectClient={onOpenClient}
                onShowIncidents={onShowIncidents}
            />

            <BeaSays panel="needs-attention">{narration.describeNeedsAttention({ priorities, unreadableCount: digest.unreadableCount })}</BeaSays>

            <div className={classes.section} data-id="worth-watching-section">
                <button
                    type="button"
                    className={classes.toggle}
                    data-id="worth-watching-toggle"
                    aria-expanded={showWatching}
                    onClick={() => setShowWatching(!showWatching)}
                >
                    Worth Watching
                    <span className={classes.count} data-id="worth-watching-count">{watching.length}</span>
                    <span className={classes.hint}>{showWatching ? 'Hide' : 'Expand'}</span>
                </button>

                {showWatching && (
                    <div className={classes.expanded}>
                        <WorthWatchingList
                            rows={listed}
                            unlistedCount={watching.length - listed.length}
                            narrowed={narrowed}
                            clientAvatar={clientAvatar}
                            clientReading={clientReading}
                            onSelectClient={onOpenClient}
                        />

                        <BeaSays panel="worth-watching">{narration.describeWorthWatching({ rows: watching, unreadableCount: digest.unreadableCount, narrowed })}</BeaSays>
                    </div>
                )}
            </div>
        </InsightsCard>
    );
};
